import * as React from "react";
import { ComponentProps } from "react";
import { Slot } from "radix-ui";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { twMergeConfig } from "@repo/utils/twMergeConfig";

export const button = tv(
  {
    base: cn(
      "ui:inline-flex ui:cursor-pointer ui:items-center ui:rounded-full ui:text-center",
      "ui:min-h-button ui:min-w-28 ui:flex-shrink-0 ui:gap-2",
      "ui:font-display ui:text-button ui:font-bold",
      "ui:transition-colors",
    ),
    variants: {
      variant: {
        primary: cn(
          "ui:justify-center ui:px-5",
          "ui:bg-actionPrimary ui:text-textInverted",
          "ui:hover:bg-actionPrimaryHover",
          "ui:active:text-onIconOutlineHover ui:active:bg-brand-600",
          "ui:disabled:cursor-default ui:disabled:bg-actionOnIconDisabled ui:disabled:text-actionIconDisabled",
        ),
        secondary: cn(
          "ui:justify-center ui:px-5",
          "ui:border-2 ui:border-actionOutline ui:text-actionOutline",
          "ui:hover:border-actionIconOutlineHover ui:hover:bg-actionOnIconHover ui:hover:text-actionOnIconOutlineHover",
          "ui:active:border-actionIconOutlineHover ui:active:bg-transparent ui:active:text-actionOnIconHover",
          "ui:disabled:cursor-default ui:disabled:border-actionIconOutlineDisabled ui:disabled:bg-transparent ui:disabled:text-actionIconOutlineDisabled",
        ),
        inverted: cn(
          "ui:justify-center ui:px-5",
          "ui:border-2 ui:border-neutral-white ui:text-textInverted",
          "ui:hover:border-actionIconOutlineHover ui:hover:text-actionIconOutlineHover",
          "ui:disabled:cursor-default ui:disabled:border-actionOnIconDisabled ui:disabled:text-actionOnIconDisabled",
        ),
        text: cn(
          "ui:gap-1 ui:rounded-none ui:px-0 ui:text-left ui:text-actionOutline",
          "ui:hover:text-actionOutlineHover",
          "ui:disabled:cursor-default ui:disabled:text-actionIconOutlineDisabled",
        ),
      },
      groupHover: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        groupHover: true,
        className: "ui:group-hover:bg-actionPrimaryHover",
      },
      {
        variant: "secondary",
        groupHover: true,
        className:
          "ui:group-hover:border-actionIconOutlineHover ui:group-hover:bg-actionOnIconHover ui:group-hover:text-actionOnIconOutlineHover",
      },
      {
        variant: "inverted",
        groupHover: true,
        className:
          "ui:group-hover:border-actionIconOutlineHover ui:group-hover:text-actionIconOutlineHover",
      },
      {
        variant: "text",
        groupHover: true,
        className: "ui:group-hover:text-actionOutlineHover",
      },
    ],
    defaultVariants: {
      variant: "primary",
      groupHover: false,
    },
  },
  {
    twMergeConfig,
  },
);

export type ButtonProps = VariantProps<typeof button> & {
  asChild?: boolean;
  className?: string;
  groupHover?: boolean;
  onClick?: ComponentProps<"button">["onClick"];
  type?: "submit" | "button";
  disabled?: boolean;
} & (
    | {
        asChild: true;
        children?: React.ReactNode;
      }
    | ({
        asChild?: false;
      } & ComponentProps<"button">)
  );

export function Button({
  asChild,
  className,
  variant,
  groupHover,
  onClick,
  type = "button",
  disabled,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot.Root : "button";

  return (
    <Component
      {...props}
      type={type}
      disabled={disabled}
      className={cn(button({ variant, groupHover }), className)}
      onClick={onClick}
    />
  );
}
